import { useRouteError } from "react-router-dom";
import { captureException, showReportDialog } from "@sentry/react";
import { memo, useEffect, useState } from "react";
import { isChunkLoadError, reloadOnceForChunkError } from "utilities-for/chunk-load-error/chunk-load-error";
import ErrorFallback from "../error-fallback";

/**
 * React Router error boundary fallback that reports errors to Sentry and
 * recovers from transient chunk loading failures.
 *
 * - For chunk load errors (typically a stale deployment), reloads the page once
 *   to fetch the current chunks, without flashing the generic crash UI or
 *   reporting to Sentry.
 * - If the reload does not resolve the failure, or for any other error, captures
 *   the exception in Sentry, opens the report dialog, and renders the shared
 *   `ErrorFallback` UI.
 *
 * @returns {JSX.Element | null} `null` while reloading after a chunk error, or the `ErrorFallback` UI otherwise.
 */
export function SentryErrorFallback() {
    const error = useRouteError();
    // Optimistically render nothing for chunk errors so we don't flash the crash
    // UI while attempting a reload; other errors show the fallback immediately.
    const [showFallback, setShowFallback] = useState(() => !isChunkLoadError(error));

    useEffect(() => {
        if (!error) {
            return;
        }

        if (isChunkLoadError(error) && reloadOnceForChunkError()) {
            // Page is reloading — don't report or render the crash UI.
            return;
        }

        // A genuine error, or a chunk error a reload did not resolve.
        setShowFallback(true);
        const eventId = captureException(error);
        showReportDialog({ eventId: eventId ?? undefined });
    }, [error]);

    if (!showFallback) {
        return null;
    }

    return <ErrorFallback />;
}

export default memo(SentryErrorFallback);
